import { createClient } from '@/lib/supabase/client';

export type Audience = 'subscribers' | 'leads' | 'members' | 'single';

export const AUDIENCE_OPTIONS: { value: Audience; label: string }[] = [
  { value: 'subscribers', label: 'Subscribers' },
  { value: 'leads', label: 'Leads who agreed to marketing' },
  { value: 'members', label: 'Current members' },
  { value: 'single', label: 'One person' },
];

export interface CampaignDraft {
  id?: string;
  name: string;
  subject: string;
  preheader: string;
  bodyMarkdown: string;
  buttonText: string;
  buttonUrl: string;
  audience: Audience;
  singleEmail: string;
  isMarketing: boolean;
}

export const EMPTY_DRAFT: CampaignDraft = {
  name: '',
  subject: '',
  preheader: '',
  bodyMarkdown: '',
  buttonText: '',
  buttonUrl: '',
  audience: 'subscribers',
  singleEmail: '',
  isMarketing: true,
};

export const TEMPLATES: { key: string; label: string; description: string; draft: CampaignDraft }[] = [
  {
    key: 'lead-invite',
    label: 'Lead invite',
    description: 'A personal invitation to Course 1 for one enquiry. Not marketing.',
    draft: {
      name: 'Lead invite',
      subject: 'Your next step with Care Business School',
      preheader: 'Start with Course 1: Is a Care Business Right for You?',
      bodyMarkdown:
        'Hi {{first_name}},\n\nThank you for your interest in starting a care business. The best place to begin is Course 1: Is a Care Business Right for You?. It helps you look honestly at your reasons, your skills and what the role will ask of you, before you spend money.\n\nIt takes about 1 hour 30 minutes and costs £49.\n\nWarm wishes,\nLinda Dube\nFounder, Care Business School',
      buttonText: 'Start Course 1',
      buttonUrl: 'https://carebusinessschool.co.uk/start?source=email',
      audience: 'single',
      singleEmail: '',
      isMarketing: false,
    },
  },
  {
    key: 'newsletter',
    label: 'Newsletter',
    description: 'A regular update for everyone on the mailing list.',
    draft: {
      name: 'Newsletter',
      subject: '',
      preheader: '',
      bodyMarkdown: 'Hi {{first_name}},\n\n\n\nWarm wishes,\nLinda Dube\nFounder, Care Business School',
      buttonText: '',
      buttonUrl: '',
      audience: 'subscribers',
      singleEmail: '',
      isMarketing: true,
    },
  },
  {
    key: 'offer',
    label: 'Offer',
    description: 'An offer for leads who agreed to marketing.',
    draft: {
      name: 'Offer',
      subject: '',
      preheader: '',
      bodyMarkdown: 'Hi {{first_name}},\n\n\n\nWarm wishes,\nLinda Dube\nFounder, Care Business School',
      buttonText: '',
      buttonUrl: '',
      audience: 'leads',
      singleEmail: '',
      isMarketing: true,
    },
  },
];

export function draftToRow(d: CampaignDraft, userId?: string | null) {
  const row: Record<string, unknown> = {
    name: d.name.trim(),
    subject: d.subject.trim(),
    preheader: d.preheader.trim() || null,
    body_markdown: d.bodyMarkdown,
    button_text: d.buttonText.trim() || null,
    button_url: d.buttonUrl.trim() || null,
    audience: d.audience,
    single_email: d.audience === 'single' ? d.singleEmail.trim().toLowerCase() : null,
    is_marketing: d.isMarketing,
  };
  if (userId) row.created_by = userId;
  return row;
}

export function rowToDraft(r: any): CampaignDraft {
  return {
    id: r.id,
    name: r.name ?? '',
    subject: r.subject ?? '',
    preheader: r.preheader ?? '',
    bodyMarkdown: r.body_markdown ?? '',
    buttonText: r.button_text ?? '',
    buttonUrl: r.button_url ?? '',
    audience: (r.audience ?? 'subscribers') as Audience,
    singleEmail: r.single_email ?? '',
    isMarketing: r.is_marketing !== false,
  };
}

export function validateDraft(d: CampaignDraft): string | null {
  if (!d.name.trim()) return 'Enter an internal name';
  if (!d.subject.trim()) return 'Enter a subject';
  if (!d.bodyMarkdown.trim()) return 'Write the email body';
  if (d.buttonUrl.trim() && !/^https:\/\//i.test(d.buttonUrl.trim())) return 'The button link must start with https://';
  if (d.buttonUrl.trim() && !d.buttonText.trim()) return 'Add button text, or clear the button link';
  if (d.buttonText.trim() && !d.buttonUrl.trim()) return 'Add a button link, or clear the button text';
  if (d.buttonText.trim().length > 60) return 'Button text must be 60 characters or fewer';
  if (d.audience === 'single' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.singleEmail.trim())) {
    return 'Enter a valid email address for the one person';
  }
  return null;
}

export async function saveCampaign(d: CampaignDraft): Promise<string> {
  const supabase = createClient();
  const { data: auth } = await supabase.auth.getUser();
  if (d.id) {
    const { error } = await supabase.from('email_campaigns').update(draftToRow(d)).eq('id', d.id);
    if (error) throw new Error(error.message);
    return d.id;
  }
  const { data, error } = await supabase
    .from('email_campaigns')
    .insert(draftToRow(d, auth?.user?.id ?? null))
    .select('id')
    .single();
  if (error) throw new Error(error.message);
  return data.id as string;
}

export interface SendResult {
  ok?: boolean;
  sent?: number;
  failed?: number;
  error?: string | null;
  note?: string;
  test?: boolean;
  sent_to?: string;
}

export async function invokeSend(campaignId: string, testEmail?: string): Promise<SendResult> {
  const supabase = createClient();
  const body: Record<string, unknown> = { campaign_id: campaignId };
  if (testEmail) body.test_email = testEmail;
  const { data, error } = await supabase.functions.invoke('send-campaign', { body });
  if (error) {
    let message = error.message;
    const ctx = (error as any)?.context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = await ctx.json();
        if (parsed?.error) message = parsed.error;
      } catch {
        // keep default message
      }
    }
    throw new Error(message);
  }
  if (data?.error && data?.ok === undefined) throw new Error(data.error);
  return (data ?? {}) as SendResult;
}

export async function countAudience(campaignId: string): Promise<number> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('campaign_audience', { p_campaign_id: campaignId });
  if (error) throw new Error(error.message);
  return Array.isArray(data) ? data.length : 0;
}

import { withUtmSource } from '@/lib/utm';

export async function subscribeToUpdates(params: { email: string; fullName: string; source: string }): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.rpc('subscribe_to_updates', {
    p_email: params.email,
    p_full_name: params.fullName,
    p_source: withUtmSource(params.source),
    p_consent: true,
  });
  if (error) throw new Error(error.message);
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  });
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Europe/London' });
}
